import type { SignupChallenge } from '../../common/hooks/use-signup-api.hook';

/**
 * The "are you human" puzzle, when Turnstile is not configured.
 *
 * The server hands out `{ salt, difficulty }`; the browser looks for the
 * smallest `nonce` (a decimal string, counting from 0) such that
 * sha256(salt + nonce) starts with `difficulty` zero bits. At the default 18
 * that is ~260k hashes — about half a second in a worker, a real cost to a
 * script that signs up thousands of times, nothing to a person.
 */

/** How many zero bits `bytes` starts with. */
export function leadingZeroBits(bytes: Uint8Array): number {
    let bits = 0;
    for (let i = 0; i < bytes.length; i++) {
        const b = bytes[i];
        if (b === 0) {
            bits += 8;
            continue;
        }
        let mask = 0x80;
        while ((b & mask) === 0) {
            bits += 1;
            mask >>= 1;
        }
        return bits;
    }
    return bits;
}

export const meetsDifficulty = (bytes: Uint8Array, difficulty: number) => leadingZeroBits(bytes) >= difficulty;

export type Sha256 = (data: Uint8Array) => Promise<ArrayBuffer | Uint8Array>;

/**
 * Find the nonce. Pure apart from the digest it is given, so it is tested
 * with Node's hash and run in the browser with SubtleCrypto.
 */
export async function solveProofOfWork(
    salt: string,
    difficulty: number,
    sha256: Sha256,
    { batch = 64, maxAttempts = 1 << 26, signal }: { batch?: number; maxAttempts?: number; signal?: { aborted: boolean } } = {},
): Promise<{ nonce: string; attempts: number }> {
    const encoder = new TextEncoder();
    for (let base = 0; base < maxAttempts; base += batch) {
        if (signal?.aborted) throw new Error('aborted');
        const hashes = await Promise.all(Array.from({ length: batch }, (_, i) => sha256(encoder.encode(salt + String(base + i)))));
        for (let i = 0; i < hashes.length; i++) {
            if (meetsDifficulty(new Uint8Array(hashes[i]), difficulty)) return { nonce: String(base + i), attempts: base + i + 1 };
        }
    }
    throw new Error('no nonce found');
}

/*
 * The same search as a Web Worker, as plain JavaScript in a string.
 *
 * A string, not a bundled module: CRA's Jest cannot parse `new URL(…,
 * import.meta.url)`, and a string runs from a blob: URL, which the CSP's
 * worker-src already allows. SubtleCrypto only exists on secure origins, so
 * the worker carries a small SHA-256 of its own for the dev rig reached over
 * plain http; both give the same answer (tested).
 */
export const POW_WORKER_SOURCE = `
var K = [
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
  0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
  0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
  0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
  0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2
];
function sha256(msg) {
  var l = msg.length, padded = ((l + 9 + 63) >> 6) << 6;
  var m = new Uint8Array(padded); m.set(msg); m[l] = 0x80;
  var dv = new DataView(m.buffer);
  dv.setUint32(padded - 4, (l * 8) >>> 0);
  dv.setUint32(padded - 8, Math.floor((l * 8) / 4294967296));
  var H = new Uint32Array([0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19]);
  var W = new Uint32Array(64), i;
  for (var off = 0; off < padded; off += 64) {
    for (i = 0; i < 16; i++) W[i] = dv.getUint32(off + i * 4);
    for (i = 16; i < 64; i++) {
      var x = W[i - 15], y = W[i - 2];
      var s0 = ((x >>> 7) | (x << 25)) ^ ((x >>> 18) | (x << 14)) ^ (x >>> 3);
      var s1 = ((y >>> 17) | (y << 15)) ^ ((y >>> 19) | (y << 13)) ^ (y >>> 10);
      W[i] = (W[i - 16] + s0 + W[i - 7] + s1) | 0;
    }
    var a = H[0], b = H[1], c = H[2], d = H[3], e = H[4], f = H[5], g = H[6], h = H[7];
    for (i = 0; i < 64; i++) {
      var S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      var ch = (e & f) ^ (~e & g);
      var t1 = (h + S1 + ch + K[i] + W[i]) | 0;
      var S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      var mj = (a & b) ^ (a & c) ^ (b & c);
      var t2 = (S0 + mj) | 0;
      h = g; g = f; f = e; e = (d + t1) | 0; d = c; c = b; b = a; a = (t1 + t2) | 0;
    }
    H[0] = (H[0] + a) | 0; H[1] = (H[1] + b) | 0; H[2] = (H[2] + c) | 0; H[3] = (H[3] + d) | 0;
    H[4] = (H[4] + e) | 0; H[5] = (H[5] + f) | 0; H[6] = (H[6] + g) | 0; H[7] = (H[7] + h) | 0;
  }
  var out = new Uint8Array(32), odv = new DataView(out.buffer);
  for (i = 0; i < 8; i++) odv.setUint32(i * 4, H[i]);
  return out;
}
function zeros(bytes, n) {
  var full = n >> 3, rest = n & 7;
  for (var i = 0; i < full; i++) if (bytes[i] !== 0) return false;
  return rest === 0 || (bytes[full] >> (8 - rest)) === 0;
}
function solve(salt, difficulty, done) {
  var enc = new TextEncoder();
  var subtle = self.crypto && self.crypto.subtle;
  if (!subtle) {
    for (var n = 0; ; n++) {
      if (zeros(sha256(enc.encode(salt + n)), difficulty)) return done(String(n), n + 1);
    }
  }
  var BATCH = 128;
  (function next(base) {
    var tries = [];
    for (var i = 0; i < BATCH; i++) tries.push(subtle.digest('SHA-256', enc.encode(salt + (base + i))));
    Promise.all(tries).then(function (hashes) {
      for (var j = 0; j < BATCH; j++) {
        if (zeros(new Uint8Array(hashes[j]), difficulty)) return done(String(base + j), base + j + 1);
      }
      next(base + BATCH);
    }, function (err) { self.postMessage({ error: String(err) }); });
  })(0);
}
self.onmessage = function (e) {
  solve(String(e.data.salt), Number(e.data.difficulty) || 0, function (nonce, attempts) {
    self.postMessage({ nonce: nonce, attempts: attempts });
  });
};
`;

/**
 * Solve a challenge off the main thread. Falls back to the main thread with
 * SubtleCrypto when workers are unavailable; rejects when neither is.
 */
export function solveChallenge(challenge: Pick<SignupChallenge, 'salt' | 'difficulty'>, signal?: AbortSignal): Promise<{ nonce: string; attempts: number }> {
    if (typeof Worker !== 'undefined' && typeof Blob !== 'undefined' && typeof URL?.createObjectURL === 'function') {
        return new Promise((resolve, reject) => {
            let url: string | null = null;
            let worker: Worker | null = null;
            const stop = () => {
                worker?.terminate();
                if (url) URL.revokeObjectURL(url);
            };
            try {
                url = URL.createObjectURL(new Blob([POW_WORKER_SOURCE], { type: 'text/javascript' }));
                worker = new Worker(url);
            } catch (e) {
                stop();
                reject(e);
                return;
            }
            signal?.addEventListener('abort', () => {
                stop();
                reject(new Error('aborted'));
            });
            worker.onmessage = (e: MessageEvent) => {
                stop();
                if (e.data?.nonce != null) resolve({ nonce: String(e.data.nonce), attempts: e.data.attempts });
                else reject(new Error(e.data?.error || 'worker failed'));
            };
            worker.onerror = (e) => {
                stop();
                reject(e);
            };
            worker.postMessage({ salt: challenge.salt, difficulty: challenge.difficulty });
        });
    }
    const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
    if (!subtle) return Promise.reject(new Error('no crypto'));
    return solveProofOfWork(challenge.salt, challenge.difficulty, (d) => subtle.digest('SHA-256', d), { signal });
}
